import React, { useCallback, useEffect, useState } from 'react'
import { tradingApi, TradingStatus } from '../services/api'
import { Loader } from '../components/UI'
import { formatDateTimeFa } from '../utils/format'
import toast from 'react-hot-toast'

/**
 * صفحه کنترل معاملات
 * دو دکمه استاپ مستقل:
 *  ۱) استاپ معاملات فروش → هیچ‌کس نمی‌تواند از ما بخرد + قیمت همه محصولات ستاره‌دار می‌شود
 *  ۲) استاپ معاملات خرید → هیچ‌کس نمی‌تواند به ما بفروشد + پنل فروش به ما کاملاً بسته می‌شود
 */
const Trading: React.FC = () => {
  const [status, setStatus] = useState<TradingStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<'sell' | 'buy' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await tradingApi.getStatus()
      if (res.data.success && res.data.data) setStatus(res.data.data)
    } catch {
      toast.error('خطا در دریافت وضعیت معاملات')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const toggle = async (which: 'sell' | 'buy') => {
    if (!status) return
    setSaving(which)
    try {
      const payload =
        which === 'sell' ? { sellStopped: !status.sellStopped } : { buyStopped: !status.buyStopped }
      const res = await tradingApi.updateStatus(payload)
      if (res.data.success && res.data.data) {
        setStatus(res.data.data)
        toast.success(res.data.message || 'وضعیت معاملات تغییر کرد')
      } else {
        toast.error(res.data.message || 'خطا')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خطا در تغییر وضعیت معاملات')
    } finally {
      setSaving(null)
    }
  }

  if (loading) return <div className="py-20 flex justify-center"><Loader /></div>
  if (!status) return <div className="py-20 text-center text-gray-500">وضعیت معاملات در دسترس نیست</div>

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black text-gray-900">کنترل معاملات (استاپ)</h1>
        <p className="text-sm text-gray-500 mt-1">
          با این دکمه‌ها می‌توانید خرید و فروش را به‌صورت لحظه‌ای متوقف کنید.
        </p>
      </div>

      {/* وضعیت کلی */}
      <div
        className={`card p-5 flex flex-wrap items-center justify-between gap-3 border-2 ${
          status.anyStopped ? 'border-red-300 bg-red-50' : 'border-green-200 bg-green-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <span className="text-3xl">{status.anyStopped ? '⛔' : '✅'}</span>
          <div>
            <div className={`font-black ${status.anyStopped ? 'text-red-800' : 'text-green-800'}`}>
              {status.anyStopped ? 'بخشی از معاملات متوقف است' : 'همه معاملات فعال است'}
            </div>
            <div className="text-xs text-gray-600 mt-0.5">
              {status.sellStopped && <span className="ml-3">• معاملات فروش: متوقف (قیمت‌ها ستاره‌دار)</span>}
              {status.buyStopped && <span className="ml-3">• معاملات خرید: متوقف (پنل فروش بسته)</span>}
              {!status.anyStopped && <span>خرید و فروش مشتریان بدون محدودیت انجام می‌شود.</span>}
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-500">
          آخرین تغییر: {status.updatedAt ? formatDateTimeFa(status.updatedAt) : '—'}
        </div>
      </div>

      {/* دو دکمه استاپ کنار هم */}
      <div className="grid md:grid-cols-2 gap-5">
        {/* استاپ معاملات فروش */}
        <div className={`card p-6 border-2 ${status.sellStopped ? 'border-red-300' : 'border-gray-100'}`}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="font-black text-gray-900 text-lg">معاملات فروش</h2>
              <p className="text-xs text-gray-500 mt-1">ما به مشتری می‌فروشیم (سفارش‌های «خرید» مشتری)</p>
            </div>
            <span className={`badge ${status.sellStopped ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
              {status.sellStopped ? 'متوقف' : 'فعال'}
            </span>
          </div>

          <ul className="text-xs text-gray-600 leading-6 bg-gray-50 rounded-lg p-4 space-y-1 mb-5">
            <li>• هیچ‌کس نمی‌تواند چیزی از ما بخرد (ثبت سفارش خرید مسدود می‌شود).</li>
            <li>• قیمت همه محصولات ستاره‌دار می‌شود و هیچ قیمتی نمایش داده نمی‌شود.</li>
            <li>• قیمت لحظه‌ای فلز در فروشگاه هم پنهان می‌شود.</li>
          </ul>

          <button
            className={`w-full !py-3 font-black ${status.sellStopped ? 'btn btn-primary' : 'btn btn-danger'}`}
            disabled={saving !== null}
            onClick={() => toggle('sell')}
          >
            {saving === 'sell'
              ? 'در حال اعمال…'
              : status.sellStopped
                ? 'فعال‌سازی معاملات فروش'
                : 'استاپ کردن معاملات فروش'}
          </button>
        </div>

        {/* استاپ معاملات خرید */}
        <div className={`card p-6 border-2 ${status.buyStopped ? 'border-red-300' : 'border-gray-100'}`}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="font-black text-gray-900 text-lg">معاملات خرید</h2>
              <p className="text-xs text-gray-500 mt-1">ما از مشتری می‌خریم (سفارش‌های «فروش» مشتری)</p>
            </div>
            <span className={`badge ${status.buyStopped ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
              {status.buyStopped ? 'متوقف' : 'فعال'}
            </span>
          </div>

          <ul className="text-xs text-gray-600 leading-6 bg-gray-50 rounded-lg p-4 space-y-1 mb-5">
            <li>• هیچ‌کس نمی‌تواند چیزی به ما بفروشد (ثبت سفارش فروش مسدود می‌شود).</li>
            <li>• پنل «فروش به ما» در فروشگاه کاملاً بسته می‌شود.</li>
            <li>• مشتری نمی‌تواند جنسی را برای فروش به ما ثبت کند.</li>
          </ul>

          <button
            className={`w-full !py-3 font-black ${status.buyStopped ? 'btn btn-primary' : 'btn btn-danger'}`}
            disabled={saving !== null}
            onClick={() => toggle('buy')}
          >
            {saving === 'buy'
              ? 'در حال اعمال…'
              : status.buyStopped
                ? 'فعال‌سازی معاملات خرید'
                : 'استاپ کردن معاملات خرید'}
          </button>
        </div>
      </div>

      <div className="card p-5 text-sm text-gray-600 leading-7">
        <b className="text-gray-900">نکته:</b>
        <br />• پنل مدیریت در هر شرایطی قیمت‌های واقعی را می‌بیند؛ ستاره‌دار شدن قیمت فقط برای مشتریان است.
        <br />• سفارش‌هایی که قبلاً ثبت شده‌اند تحت تأثیر استاپ قرار نمی‌گیرند و می‌توانید وضعیت آن‌ها را در صفحه «سفارش‌ها» مدیریت کنید.
        <br />• هر دو استاپ مستقل هستند و می‌توانند هم‌زمان فعال شوند.
      </div>
    </div>
  )
}

export default Trading
